import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, CheckCircle, X } from 'lucide-react';
import '../../styles/admin.scss';
import axios from '../../helpers/axiosconfig';
import toast from 'react-hot-toast';

export const AdminOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/admin/orders');

      if (response.data.success) {
        setOrders(response.data.orders);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch orders');
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const handleConfirmPaymentClick = (order) => {
    setSelectedOrder(order);
    setShowConfirmModal(true);
  };

  const confirmPayment = async () => {
    if (!selectedOrder) return;

    setConfirmLoading(true);
    try {
      const response = await axios.put(
        `/admin/orders/${selectedOrder._id}/confirm-payment`
      );

      if (response.data.success) {
        toast.success('Payment confirmed successfully');
        setShowConfirmModal(false);
        setSelectedOrder(null);
        // Refresh orders list
        fetchOrders();
      } else {
        toast.error(response.data.message || 'Failed to confirm payment');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to confirm payment';
      toast.error(errorMsg);
      console.error('Error confirming payment:', err);
    } finally {
      setConfirmLoading(false);
    }
  };

  const closeModal = () => {
    if (!confirmLoading) {
      setShowConfirmModal(false);
      setSelectedOrder(null);
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'admin-orders__status--delivered';
      case 'processing':
        return 'admin-orders__status--processing';
      case 'shipped':
        return 'admin-orders__status--shipped';
      default:
        return '';
    }
  };

  const formatAddress = (shippingAddress) => {
    if (!shippingAddress) return 'N/A';
    const { name, street, city, state, pincode } = shippingAddress;
    return `${name}, ${street}, ${city}, ${state} - ${pincode}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const canConfirmPayment = (order) => {
    return order.paymentStatus === 'pending';
  };

  const getCustomerName = (order) => {
    if (order.userId && typeof order.userId === 'object') {
      return order.userId.name || order.userId.email || 'N/A';
    }
    return order.shippingAddress?.name || 'N/A';
  };

  return (
    <div className="admin-orders">
      <nav className="admin-orders__navbar">
        <div className="admin-orders__navbar-content">
          <h1 className="admin-orders__navbar-title">Admin Dashboard</h1>
          <button
            onClick={handleLogout}
            className="admin-orders__navbar-logout"
          >
            Logout
          </button>
        </div>
      </nav>

      <main className="admin-orders__content">
        <div className="admin-orders__card">
          <h2 className="admin-orders__title">Orders Management</h2>

          {loading && (
            <div className="admin-orders__loading">Loading orders...</div>
          )}

          {error && (
            <div className="admin-orders__error">
              Error: {error}
            </div>
          )}

          {!loading && !error && (
            <div className="admin-orders__table-container">
              <table className="admin-orders__table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Address</th>
                    <th>Payment Mode</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Payment Status</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <React.Fragment key={order._id}>
                      <tr>
                        <td>{order.orderId || order._id}</td>
                        <td>{getCustomerName(order)}</td>
                        <td>{formatAddress(order.shippingAddress)}</td>
                        <td>
                          <span className="admin-orders__payment-mode">
                            {order.paymentMethod === 'cod' ? 'COD' : 'Prepaid'}
                          </span>
                        </td>
                        <td>{formatDate(order.createdAt)}</td>
                        <td>
                          <span className={`admin-orders__status ${getStatusClass(order.status)}`}>
                            {order.status}
                          </span>
                        </td>
                        <td>
                          <div className="admin-orders__payment-status-cell">
                            <span className="admin-orders__payment-status">
                              {order.paymentStatus}
                            </span>
                            {canConfirmPayment(order) && (
                              <button
                                onClick={() => handleConfirmPaymentClick(order)}
                                className="admin-orders__confirm-btn"
                                title="Confirm payment received"
                                aria-label="Confirm payment"
                              >
                                <CheckCircle size={16} />
                                Confirm
                              </button>
                            )}
                          </div>
                        </td>
                        <td>{formatCurrency(order.totalAmount)}</td>
                        <td>
                          <button
                            onClick={() => toggleOrderExpansion(order._id)}
                            className="admin-orders__expand-btn"
                          >
                            {expandedOrders[order._id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                          </button>
                        </td>
                      </tr>
                      {expandedOrders[order._id] && (
                        <tr className="admin-orders__expanded-row">
                          <td colSpan="9">
                            <div className="admin-orders__expanded-content">
                              <h3 className="admin-orders__subtitle">Order Details</h3>
                              {order.shipping?.courier && (
                                <p>Courier Service: {order.shipping.courier}</p>
                              )}
                              {order.shipping?.shipmentId && (
                                <p>Shipment ID: {order.shipping.shipmentId}</p>
                              )}
                              <h3 className="admin-orders__subtitle">Products</h3>
                              <table className="admin-orders__products-table">
                                <thead>
                                  <tr>
                                    <th>Product</th>
                                    <th>Category</th>
                                    <th>Volume</th>
                                    <th>Quantity</th>
                                    <th>Price</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {order.items && order.items.map((item) => {
                                    const variant = item.productId?.variations?.find(v => v._id === item.variationId);
                                    return (
                                      <tr key={item._id}>
                                        <td>{variant?.name || item.productId?.name || 'N/A'}</td>
                                        <td>{item.productId?.category || 'N/A'}</td>
                                        <td>{variant?.volume || 'N/A'}</td>
                                        <td>{item.quantity}</td>
                                        <td>{formatCurrency(item.price)}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Confirmation Modal */}
      {showConfirmModal && selectedOrder && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Payment Received</h3>
              <button
                onClick={closeModal}
                className="modal-close-btn"
                disabled={confirmLoading}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-info">
                <div className="info-row">
                  <span className="info-label">Order ID:</span>
                  <span className="info-value">{selectedOrder.orderId}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Customer:</span>
                  <span className="info-value">{getCustomerName(selectedOrder)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Total Amount:</span>
                  <span className="info-value amount">
                    {formatCurrency(selectedOrder.totalAmount)}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Payment Method:</span>
                  <span className="info-value">{selectedOrder.paymentMethod === 'cod' ? 'COD (Cash on Delivery)' : 'Prepaid'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Current Status:</span>
                  <span className="info-value">
                    {selectedOrder.paymentStatus}
                  </span>
                </div>
              </div>

              <div className="modal-warning">
                <div className="warning-icon">⚠️</div>
                <p>
                  Are you sure you want to mark this payment as received? This action will
                  update the payment status to "Completed" and cannot be easily undone.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={closeModal}
                className="btn-secondary"
                disabled={confirmLoading}
              >
                Cancel
              </button>
              <button
                onClick={confirmPayment}
                className="btn-primary"
                disabled={confirmLoading}
              >
                {confirmLoading ? (
                  <>
                    <div className="btn-spinner"></div>
                    Confirming...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Confirm Payment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
